import { useTranslation } from "react-i18next";
import { Divider, Form, Grid, GridColumn, Icon, Modal } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import { SEASONS } from "models";
import { EAN_VALIDATION, enumToDropdownOptions, isKnownManufacturer } from "utils/common";

import type { DropdownItemProps } from "components";
import type { Tyre } from "models";

export type TyreFormValues = Pick<Tyre, "id" | "manufacturer" | "description" | "speed" | "season" | "run_flat_tire"> & {
  ean: string;
  carry_capacity: number | null;
  width: number | null;
  height: number | null;
  size: number | null;
};

type TyreFormProps = {
  isEdit: boolean;
  isPending: boolean;
  brandOptions: DropdownItemProps<string>[];
  onClose: () => void;
};

const FormHelper = createFormHelper<TyreFormValues>();

export const TyreForm = ({ isEdit, isPending, brandOptions, onClose }: TyreFormProps) => {
  const { t } = useTranslation();
  const { setValue, watch, formState } = FormHelper.useFormContext();

  const tyreSeasonOptions = enumToDropdownOptions({ enumObj: SEASONS, t, keysToExclude: ["NoSeason", "TruckSeason"] });
  const [manufacturer, description, width, size, speed, height, ean, carryCapacity] = watch([
    "manufacturer",
    "description",
    "width",
    "size",
    "speed",
    "height",
    "ean",
    "carry_capacity"
  ]);
  const isEanInvalid = formState.isSubmitted && !EAN_VALIDATION.test(ean);

  return (
    <>
      <Modal.Header className="padding-container">
        <div className="header-title">{isEdit ? t("v8_edit_tyre", "Edit tyre") : t("v8_add_tyre", "Add tyre")}</div>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content className="padding-container">
        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={formState.isSubmitted && !isKnownManufacturer(brandOptions, manufacturer)}>
              <label className="no-wrap-text">{t("v8_manufacturer", "Manufacturer")}</label>
              <FormHelper.Field.Dropdown
                name="manufacturer"
                options={brandOptions}
                dropdownProps={{ placeholder: t("v8_manufacturer", "Manufacturer"), fluid: true, selection: true, search: true, selectOnBlur: false }}
              />
            </Form.Field>
          </GridColumn>
          <GridColumn>
            <Form.Field required error={formState.isSubmitted && !description}>
              <label className="no-wrap-text">{t("v8_description", "Description")}</label>
              <FormHelper.Field.Input
                name="description"
                onChange={value => setValue("description", value.toUpperCase())}
                inputProps={{ placeholder: t("v8_description", "Description"), fluid: true }}
              />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns="equal">
          <GridColumn width={2}>
            <Form.Field required error={formState.isSubmitted && !width}>
              <label className="no-wrap-text">{t("v8_width", "Width")}</label>
              <FormHelper.Field.Input name="width" nullable inputProps={{ placeholder: t("v8_width", "Width"), fluid: true, type: "number" }} />
            </Form.Field>
          </GridColumn>
          <GridColumn width={2}>
            <Form.Field required error={formState.isSubmitted && !size}>
              <label className="no-wrap-text">{t("v8_size", "Size")}</label>
              <FormHelper.Field.Input name="size" nullable inputProps={{ placeholder: t("v8_size", "Size"), fluid: true, type: "number" }} />
            </Form.Field>
          </GridColumn>
          <GridColumn width={2}>
            <Form.Field required error={formState.isSubmitted && !speed}>
              <label className="no-wrap-text">{t("v8_speed", "Speed")}</label>
              <FormHelper.Field.Input
                name="speed"
                onChange={value => setValue("speed", value.toUpperCase())}
                inputProps={{ placeholder: t("v8_speed", "Speed"), fluid: true }}
              />
            </Form.Field>
          </GridColumn>
          <GridColumn width={2}>
            <Form.Field required error={formState.isSubmitted && !height}>
              <label className="no-wrap-text">{t("v8_height", "Height")}</label>
              <FormHelper.Field.Input name="height" nullable inputProps={{ placeholder: t("v8_height", "Height"), fluid: true, type: "number" }} />
            </Form.Field>
          </GridColumn>
          <GridColumn width={5}>
            <Form.Field required error={isEanInvalid}>
              <label className="no-wrap-text">EAN</label>
              <FormHelper.Field.Input name="ean" inputProps={{ placeholder: "EAN", fluid: true }} />
              {isEanInvalid && <div className="field-error">{t("v8_ean_must_be_13_digits", "EAN must be 13 digits")}</div>}
            </Form.Field>
          </GridColumn>
          <GridColumn>
            <Form.Field required error={formState.isSubmitted && !carryCapacity}>
              <label className="no-wrap-text">{t("v8_carry_capacity", "Carry capacity")}</label>
              <FormHelper.Field.Input
                name="carry_capacity"
                nullable
                inputProps={{ placeholder: t("v8_carry_capacity", "Carry capacity"), fluid: true, type: "number" }}
              />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns="equal">
          <GridColumn>
            <Form.Field>
              <label>{t("v8_season", "Season")}</label>
              <FormHelper.Field.Dropdown name="season" options={tyreSeasonOptions} dropdownProps={{ fluid: true, selection: true, selectOnBlur: false }} />
            </Form.Field>
          </GridColumn>
          <GridColumn verticalAlign="middle">
            <FormHelper.Field.Checkbox name="run_flat_tire" label={t("v8_run_flat_tire", "Run flat tire")} />
          </GridColumn>
        </Grid>
      </Modal.Content>
      <Divider fitted />

      <Modal.Actions className="padding-container">
        <div className="save-cancel-buttons-container">
          <Form.Button type="button" className="light" onClick={onClose}>
            {t("v8_cancel", "Cancel")}
            <Icon className="xmark" />
          </Form.Button>
          <Form.Button type="submit" color="green" disabled={isPending} loading={isPending}>
            {t("v8_save", "Save")}
            <Icon className="check" />
          </Form.Button>
        </div>
      </Modal.Actions>
    </>
  );
};
